/**
 * "Mark complete" for an activity — one popup, three forms:
 *   call  → start / end / duration, outcome, notes
 *   visit → date, times, location + GPS, purpose, notes, feedback, next action
 *   other → a note
 * Mounted once in AppShell and opened from every place that can complete an
 * activity (`useOpenComplete`), the same way the lead qualification gate is.
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { closeCompleteGate, openCompleteGate } from '../ui/uiSlice.js';
import { useCompleteActivity } from './useActivities.js';

export const ACTIVITY_COMPLETED_EVENT = 'activity-completed';

const CALL_OUTCOMES = ['Interested', 'Not Interested', 'Call Back', 'Meeting Required', 'Quotation Required'];
const VISIT_PURPOSES = [
  'Requirement Discussion', 'Product Demo', 'Negotiation', 'Technical Discussion', 'Payment Follow-up', 'Relationship Visit',
];

const pad = (n) => String(n).padStart(2, '0');
const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeInput = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toLocalInput = (d) => `${toDateInput(d)}T${toTimeInput(d)}`;
const minutesBetween = (a, b) => (a && b ? Math.round((b - a) / 60000) : null);

export function useOpenComplete() {
  const dispatch = useDispatch();
  return (activity) => dispatch(openCompleteGate(activity));
}

function initialState(a) {
  const start = new Date(a.scheduled_start ?? a.due_at ?? Date.now());
  const end = a.scheduled_end ? new Date(a.scheduled_end) : null;
  return {
    callStart: toLocalInput(start), callEnd: end ? toLocalInput(end) : '',
    outcome: '', notes: '',
    visitDate: toDateInput(start), startTime: toTimeInput(start), endTime: end ? toTimeInput(end) : '',
    location: a.location ?? '', gps: '', purpose: '', feedback: '', nextAction: '', nextDate: '',
  };
}

export function CompleteActivityModal() {
  const dispatch = useDispatch();
  const activity = useSelector((s) => s.ui.completeGate);
  const complete = useCompleteActivity();
  const [v, setV] = useState({});
  const [errors, setErrors] = useState({});
  const [locating, setLocating] = useState(false);

  useEffect(() => { if (activity) { setV(initialState(activity)); setErrors({}); } }, [activity?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!activity) return null;

  const kind = activity.kind === 'call' || activity.kind === 'visit' ? activity.kind : 'other';
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const close = () => dispatch(closeCompleteGate());

  const callMinutes = minutesBetween(new Date(v.callStart), v.callEnd ? new Date(v.callEnd) : null);
  const visitStart = v.visitDate && v.startTime ? new Date(`${v.visitDate}T${v.startTime}`) : null;
  const visitEnd = v.visitDate && v.endTime ? new Date(`${v.visitDate}T${v.endTime}`) : null;
  const visitMinutes = minutesBetween(visitStart, visitEnd);

  const useMyLocation = () => {
    if (!navigator.geolocation) { setErrors((e) => ({ ...e, gps: 'This browser cannot share a location.' })); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setV((x) => ({ ...x, gps: `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}` }));
        setLocating(false);
      },
      () => { setLocating(false); setErrors((e) => ({ ...e, gps: 'Could not get your location — type it in instead.' })); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    const body = { rowVersion: activity.row_version };

    if (kind === 'call') {
      if (!v.callStart) errs.callStart = 'Required';
      if (!v.callEnd) errs.callEnd = 'Required';
      else if (callMinutes < 0) errs.callEnd = 'Must be after the start';
      if (!v.outcome) errs.outcome = 'Choose an outcome';
      if (!Object.keys(errs).length) {
        body.outcome = v.outcome;
        if (v.notes.trim()) body.outcome_note = v.notes.trim();
        body.duration_minutes = callMinutes;
        body.completed_at = new Date(v.callEnd).toISOString();
        body.details = { call_start: new Date(v.callStart).toISOString(), call_end: new Date(v.callEnd).toISOString(), call_outcome: v.outcome };
      }
    } else if (kind === 'visit') {
      if (!v.visitDate) errs.visitDate = 'Required';
      if (!v.startTime) errs.startTime = 'Required';
      if (!v.endTime) errs.endTime = 'Required';
      else if (visitMinutes != null && visitMinutes < 0) errs.endTime = 'Must be after the start';
      if (!v.location.trim()) errs.location = 'Required';
      if (!v.purpose) errs.purpose = 'Choose a purpose';
      if (v.nextAction.trim() && !v.nextDate) errs.nextDate = 'Pick a date for the next action';
      if (!Object.keys(errs).length) {
        body.outcome = v.purpose;
        if (v.notes.trim()) body.outcome_note = v.notes.trim();
        body.duration_minutes = visitMinutes;
        body.completed_at = visitEnd.toISOString();
        body.location = v.location.trim();
        body.details = {
          visit_date: v.visitDate, start_time: v.startTime, end_time: v.endTime,
          gps_location: v.gps.trim() || null, purpose: v.purpose,
          customer_feedback: v.feedback.trim() || null,
          next_action: v.nextAction.trim() || null, next_follow_up_date: v.nextDate || null,
        };
        if (v.nextDate) {
          body.next_activity = {
            kind: 'task',
            subject: v.nextAction.trim() || `Follow up: ${activity.subject}`,
            due_at: new Date(`${v.nextDate}T09:00`).toISOString(),
          };
        }
      }
    } else if (v.notes.trim()) {
      body.outcome_note = v.notes.trim();
    }

    setErrors(errs);
    if (Object.keys(errs).length) return;
    complete.mutate({ id: activity.id, ...body }, {
      onSuccess: () => {
        window.dispatchEvent(new CustomEvent(ACTIVITY_COMPLETED_EVENT, { detail: activity.id }));
        close();
      },
    });
  };

  const area = (k, label, rows = 3, placeholder) => (
    <Field label={label}>
      <textarea className="form-control" rows={rows} value={v[k] ?? ''} onChange={set(k)} placeholder={placeholder} />
    </Field>
  );

  return (
    <Modal
      open
      onClose={close}
      title={`Complete ${kind === 'call' ? 'call' : kind === 'visit' ? 'visit' : 'activity'}`}
      size={kind === 'visit' ? 'lg' : undefined}
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={close}>Cancel</button>
          <button type="submit" form="completeForm" className="btn btn-save btn-sm-x" disabled={complete.isPending}>
            {complete.isPending ? <><ButtonSpinner />Saving…</> : 'Mark complete'}
          </button>
        </>
      )}
    >
      <form id="completeForm" onSubmit={submit} noValidate>
        <div className="t-sm text-muted mb-3">{activity.subject}</div>

        {kind === 'call' && (
          <div className="row g-3">
            <div className="col-md-4">
              <Field label="Call start" required error={errors.callStart}>
                <input type="datetime-local" className="form-control" value={v.callStart ?? ''} onChange={set('callStart')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Call end" required error={errors.callEnd}>
                <input type="datetime-local" className="form-control" value={v.callEnd ?? ''} onChange={set('callEnd')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Duration">
                <input className="form-control" readOnly disabled value={callMinutes != null && callMinutes >= 0 ? `${callMinutes} min` : '—'} />
              </Field>
            </div>
            <div className="col-12">
              <Field label="Call outcome" required error={errors.outcome}>
                <select className="form-select" value={v.outcome ?? ''} onChange={set('outcome')}>
                  <option value="">Select an outcome…</option>
                  {CALL_OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
            </div>
            <div className="col-12">{area('notes', 'Notes')}</div>
          </div>
        )}

        {kind === 'visit' && (
          <div className="row g-3">
            <div className="col-md-4">
              <Field label="Visit date" required error={errors.visitDate}>
                <input type="date" className="form-control" value={v.visitDate ?? ''} onChange={set('visitDate')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Start time" required error={errors.startTime}>
                <input type="time" className="form-control" value={v.startTime ?? ''} onChange={set('startTime')} />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="End time" required error={errors.endTime} help={visitMinutes != null && visitMinutes >= 0 ? `Duration ${visitMinutes} min` : undefined}>
                <input type="time" className="form-control" value={v.endTime ?? ''} onChange={set('endTime')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Location" required error={errors.location}>
                <input className="form-control" value={v.location ?? ''} onChange={set('location')} maxLength={500} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="GPS location" error={errors.gps}>
                <div className="input-group">
                  <input className="form-control" value={v.gps ?? ''} onChange={set('gps')} placeholder="latitude, longitude" />
                  <button type="button" className="btn btn-edit btn-sm-x" onClick={useMyLocation} disabled={locating}>
                    {locating ? <ButtonSpinner /> : <i className="bi bi-geo-alt" aria-hidden="true" />}<span className="ms-1">Use mine</span>
                  </button>
                </div>
              </Field>
            </div>
            <div className="col-12">
              <Field label="Purpose" required error={errors.purpose}>
                <select className="form-select" value={v.purpose ?? ''} onChange={set('purpose')}>
                  <option value="">Select a purpose…</option>
                  {VISIT_PURPOSES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
            </div>
            <div className="col-md-6">{area('notes', 'Visit notes')}</div>
            <div className="col-md-6">{area('feedback', 'Customer feedback')}</div>
            <div className="col-md-6">
              <Field label="Next action">
                <input className="form-control" value={v.nextAction ?? ''} onChange={set('nextAction')} maxLength={300} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Next follow-up date" error={errors.nextDate} help="Creates a follow-up task for that day.">
                <input type="date" className="form-control" value={v.nextDate ?? ''} onChange={set('nextDate')} />
              </Field>
            </div>
          </div>
        )}

        {kind === 'other' && area('notes', 'Note', 4, 'What happened? (optional)')}
      </form>
    </Modal>
  );
}
